import { evaluateCompletion } from "./completion-validation.js";
/**
 * Engine-independent click-to-append session for symbols whose semantic model
 * uses a variable or fixed sequence of control points.
 *
 * Draft output is emitted only when the committed points plus pointer preview
 * satisfy minimumPoints, or when the Definition supplies a complete transient
 * control set derived from the committed controls. Derived draft controls are
 * never used for completion or persisted state.
 */
export class MultiPointDrawSession {
    #options;
    #minimumPoints;
    #maximumPoints;
    #completeAtMaximum;
    #status = "ready";
    #points = [];
    #cursor;
    #completed;
    #rejection;
    constructor(options) {
        validateFeatureOptions(options);
        const isVariableTwoPointPath = options.minimumPoints === 2 &&
            options.maximumPoints !== undefined &&
            options.maximumPoints > 2;
        const minimumAllowed = isVariableTwoPointPath ? 2 : 3;
        if (!Number.isInteger(options.minimumPoints) ||
            options.minimumPoints < minimumAllowed) {
            throw new RangeError(`MultiPointDrawSession minimumPoints must be an integer >= ${minimumAllowed}.`);
        }
        if (options.maximumPoints !== undefined &&
            (!Number.isInteger(options.maximumPoints) ||
                options.maximumPoints < options.minimumPoints)) {
            throw new RangeError("MultiPointDrawSession maximumPoints must be an integer >= minimumPoints.");
        }
        this.#options = options;
        this.#minimumPoints = options.minimumPoints;
        this.#maximumPoints = options.maximumPoints;
        this.#completeAtMaximum = options.completeAtMaximum ?? true;
    }
    get status() {
        return this.#status;
    }
    snapshot() {
        if (this.#status === "completed" && this.#completed) {
            return { status: this.#status, completed: this.#completed };
        }
        const draft = this.#createDraft();
        return {
            status: this.#status,
            ...(draft ? { draft } : {}),
            ...(this.#rejection ? { rejection: this.#rejection } : {}),
        };
    }
    click(position) {
        if (this.#isTerminal() || this.#isAtMaximum()) {
            return this.snapshot();
        }
        const point = clonePosition(position);
        const last = this.#points.at(-1);
        if (last && samePosition(last, point)) {
            return this.snapshot();
        }
        this.#rejection = undefined;
        const candidate = [...this.#points, point];
        if (this.#completeAtMaximum &&
            this.#maximumPoints !== undefined &&
            candidate.length === this.#maximumPoints) {
            const completed = this.#tryComplete(candidate);
            if (completed)
                return completed;
            // Keep the rejected final point as a live pointer candidate instead of
            // filling the fixed-count session and trapping it at maximumPoints.
            this.#cursor = point;
            this.#status = "drawing";
            return this.snapshot();
        }
        this.#points = candidate;
        this.#cursor = undefined;
        this.#status = "drawing";
        return this.snapshot();
    }
    doubleClick(position) {
        if (this.#isTerminal()) {
            return this.snapshot();
        }
        const point = clonePosition(position);
        const last = this.#points.at(-1);
        const candidate = last && samePosition(last, point)
            ? [...this.#points]
            : this.#isAtMaximum()
                ? [...this.#points]
                : [...this.#points, point];
        this.#cursor = undefined;
        this.#rejection = undefined;
        if (candidate.length >= this.#minimumPoints) {
            const completed = this.#tryComplete(candidate);
            if (completed)
                return completed;
        }
        if (this.#maximumPoints !== undefined &&
            candidate.length >= this.#maximumPoints &&
            !(last && samePosition(last, point))) {
            this.#points = candidate
                .slice(0, this.#maximumPoints - 1)
                .map(clonePosition);
            this.#cursor = point;
        }
        else {
            this.#points = candidate.map(clonePosition);
        }
        this.#status = this.#points.length > 0 ? "drawing" : "ready";
        return this.snapshot();
    }
    pointerMove(position) {
        if (this.#isTerminal() || this.#points.length === 0) {
            return this.snapshot();
        }
        const point = clonePosition(position);
        const last = this.#points.at(-1);
        this.#rejection = undefined;
        this.#cursor =
            !last || samePosition(last, point) || this.#isAtMaximum()
                ? undefined
                : point;
        return this.snapshot();
    }
    keyDown(key) {
        if (this.#isTerminal()) {
            return this.snapshot();
        }
        if (key === "Escape") {
            return this.cancel();
        }
        if (key === "Backspace" || key === "Delete") {
            this.#points = this.#points.slice(0, -1);
            this.#cursor = undefined;
            this.#rejection = undefined;
            this.#status = this.#points.length === 0 ? "ready" : "drawing";
            return this.snapshot();
        }
        if (key === "Enter") {
            const candidate = this.#candidatePoints();
            this.#rejection = undefined;
            if (candidate.length >= this.#minimumPoints) {
                const completed = this.#tryComplete(candidate);
                if (completed)
                    return completed;
            }
        }
        return this.snapshot();
    }
    cancel() {
        if (this.#status !== "completed") {
            this.#status = "cancelled";
            this.#points = [];
            this.#cursor = undefined;
            this.#rejection = undefined;
        }
        return this.snapshot();
    }
    #createDraft() {
        if (this.#isTerminal()) {
            return undefined;
        }
        const candidate = this.#candidatePoints();
        if (candidate.length >= this.#minimumPoints) {
            return this.#createFeature(candidate);
        }
        if (this.#cursor || !this.#options.deriveDraftControlPoints) {
            return undefined;
        }
        try {
            const derived = this.#options.deriveDraftControlPoints(this.#points.map(clonePosition));
            if (!derived || derived.length < this.#minimumPoints) {
                return undefined;
            }
            if (this.#maximumPoints !== undefined &&
                derived.length > this.#maximumPoints) {
                return undefined;
            }
            return this.#createFeature(derived.map(clonePosition));
        }
        catch {
            return undefined;
        }
    }
    #candidatePoints() {
        if (!this.#cursor || this.#isAtMaximum()) {
            return this.#points;
        }
        const last = this.#points.at(-1);
        return last && samePosition(last, this.#cursor)
            ? this.#points
            : [...this.#points, this.#cursor];
    }
    #tryComplete(points) {
        const limited = this.#maximumPoints === undefined
            ? [...points]
            : points.slice(0, this.#maximumPoints);
        if (limited.length < this.#minimumPoints) {
            return undefined;
        }
        const candidate = this.#createFeature(limited);
        const evaluation = evaluateCompletion(this.#options.validateCompletion, candidate);
        if (!evaluation.valid) {
            this.#rejection = evaluation.rejection;
            return undefined;
        }
        this.#completed = candidate;
        this.#points = limited.map(clonePosition);
        this.#cursor = undefined;
        this.#rejection = undefined;
        this.#status = "completed";
        return this.snapshot();
    }
    #createFeature(controlPoints) {
        const input = {
            id: this.#options.id,
            plotType: this.#options.plotType,
            controlPoints: controlPoints.map(clonePosition),
        };
        return {
            ...input,
            ...(this.#options.definitionVersion !== undefined
                ? { definitionVersion: this.#options.definitionVersion }
                : {}),
            ...(this.#options.parameters !== undefined
                ? { parameters: { ...this.#options.parameters } }
                : {}),
            ...(this.#options.style !== undefined
                ? { style: { ...this.#options.style } }
                : {}),
            ...(this.#options.metadata !== undefined
                ? { metadata: { ...this.#options.metadata } }
                : {}),
        };
    }
    #isAtMaximum() {
        return (this.#maximumPoints !== undefined &&
            this.#points.length >= this.#maximumPoints);
    }
    #isTerminal() {
        return this.#status === "completed" || this.#status === "cancelled";
    }
}
function validateFeatureOptions(options) {
    if (!options.id.trim()) {
        throw new TypeError("MultiPointDrawSession id must not be empty.");
    }
    if (!options.plotType.trim()) {
        throw new TypeError("MultiPointDrawSession plotType must not be empty.");
    }
}
function clonePosition([longitude, latitude]) {
    return [longitude, latitude];
}
function samePosition(left, right) {
    return left[0] === right[0] && left[1] === right[1];
}
//# sourceMappingURL=multi-point-draw-session.js.map